import {
  INPMetric,
  LCPMetric,
  TTFBMetric,
  onINP,
  onLCP,
  onTTFB,
} from 'web-vitals';

import bridge from '../../bridge/bridge';
import { NotifyBarcodeScannedRequest } from '../device/actions';
import { SDKAction } from '../types/actions';
import {
  AuthorizeRequest,
  AuthorizeResponse,
  InitializeRequest,
  InitializeResponse,
} from './actions';
import { Context, SDKActionCallbacks, SdkApi } from './types';
import {
  log,
  InpPerformanceReport,
  LcpPerformanceReport,
  Rating,
  TtfbPerformanceReport,
} from '../log';
import { NetworkPerformanceReport } from '../log/types';

export const REVERSE_PROXY_TOKEN_EXPIRED_HEADER =
  'X-Reverse-Proxy-JWT-Expired-Error';

/**
 * Builds the Square reverse proxy URL for the provided targetUrl.
 *
 * @param targetUrl The URL that the 3P is requesting to fetch.
 * @returns Square reverse proxy URL that will proxy the 3P's request.
 */
function buildReverseProxyUrl(targetUrl: RequestInfo | URL): string {
  const { url: reverseProxyUrl } = bridge.addon.getReverseProxyData();
  const encodedTargetUrl = encodeURI(targetUrl.toString());
  return `${reverseProxyUrl}?target_url=${encodedTargetUrl}`;
}

/**
 * Attaches the Authorization header to request with the Square reverse proxy token.
 *
 * @param options The fetch options specified by the 3P.
 * @returns options with the Authorization header set to bear the Square
 *          reverse proxy token.
 */
function buildOptionsWithReverseProxyToken(options?: RequestInit): RequestInit {
  return {
    ...options,
    headers: {
      ...options?.headers,
      Authorization: `Bearer ${bridge.addon.getReverseProxyData().token}`,
    },
  };
}

/**
 * Initializes the SDK.
 *
 * @param callbacks Callbacks for SDK actions.
 * @returns Context object for the POS.
 * @throws {RequestError}
 */
async function initialize(callbacks: SDKActionCallbacks): Promise<Context> {
  // Skip overwriting window.fetch if there were previous sdk.initialize calls
  if (!window.Square?.bridge) {
    // Proxies 3P fetch requests through the Square reverse proxy.
    // We need to send all network calls (including 3P API calls) through the Square
    // reverse proxy to bypass the firewall on Square hardware.
    const originalFetch = window.fetch;
    window.fetch = async (
      targetUrl: RequestInfo | URL,
      options?: RequestInit
    ): Promise<Response> => {
      let squareURL = buildReverseProxyUrl(targetUrl);
      let squareOptions = buildOptionsWithReverseProxyToken(options);
      const originalPromise = await originalFetch(squareURL, squareOptions);

      if (!originalPromise.headers.get(REVERSE_PROXY_TOKEN_EXPIRED_HEADER)) {
        return originalPromise;
      }

      // If the token has expired, fetch a new one and retry the request
      const response = (await bridge.app.postMessage(
        new AuthorizeRequest()
      )) as AuthorizeResponse;

      bridge.addon.setReverseProxyData(response.value.reverseProxyData);
      squareURL = buildReverseProxyUrl(targetUrl);
      squareOptions = buildOptionsWithReverseProxyToken(options);

      return originalFetch(squareURL, squareOptions);
    };

    onLCP(
      (metric: LCPMetric) => {
        const lcpPerformanceReport: LcpPerformanceReport = {
          value: metric.value,
          rating: <Rating>metric.rating,
          metadata: {
            element: metric.entries[0]?.element?.outerHTML,
            size: metric.entries[0]?.size,
            renderTime: metric.entries[0]?.renderTime,
            startTime: metric.entries[0]?.startTime,
          },
        };

        log.logLcpPerformance(lcpPerformanceReport);
      },
      {
        reportAllChanges: false, // Only interested in the last LCP since that value measures the initial render time.
      }
    );

    onTTFB((metric: TTFBMetric) => {
      const ttfbPerformanceReport: TtfbPerformanceReport = {
        value: metric.value,
        rating: <Rating>metric.rating,
        metadata: {
          requestStart: metric.entries[0]?.requestStart,
          domComplete: metric.entries[0]?.domComplete,
        },
      };

      log.logTtfbPerformance(ttfbPerformanceReport);
    });

    onINP(
      (metric: INPMetric) => {
        const inpPerformanceReport: InpPerformanceReport = {
          value: metric.value,
          rating: <Rating>metric.rating,
          metadata: {
            processingStart: metric.entries[0]?.processingStart,
            processingEnd: metric.entries[0]?.processingEnd,
            startTime: metric.entries[0]?.startTime,
          },
        };

        log.logInpPerformance(inpPerformanceReport);
      },
      { reportAllChanges: true }
    );

    if (window.PerformanceObserver) {
      // Log the fetch performance.
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry: PerformanceResourceTiming) => {
          if (entry.initiatorType === 'fetch') {
            const params = new URL(entry.name).searchParams;
            const targetUrl = params.get('target_url');

            const networkPerformanceReport: NetworkPerformanceReport = {
              value: entry.duration,
              metadata: {
                url: targetUrl,
                tcpHandshakeTime: entry.connectEnd - entry.connectStart,
                dnsLookupTime: entry.domainLookupEnd - entry.domainLookupStart,
                requestTime: entry.responseStart - entry.requestStart,
                fetchTime: entry.responseEnd - entry.fetchStart,
              },
            };

            log.logNetworkPerformance(networkPerformanceReport);
          }
        });
      });

      observer.observe({ type: 'resource', buffered: true });
    }

    // Log any uncaught errors.
    window.addEventListener('error', (errorEvent: ErrorEvent) => {
      const message = {
        message: errorEvent.message,
        error: errorEvent.error,
      };

      log.logUncaughtError(JSON.stringify(message));
    });

    // Log any unhandled promise rejections.
    window.addEventListener(
      'unhandledrejection',
      (promiseRejectionEvent: PromiseRejectionEvent) => {
        log.logUnhandledRejection(JSON.stringify(promiseRejectionEvent.reason));
      }
    );
  }

  // Web Payments SDK also establishes the window.Square namespace.
  // To avoid namespace collisions, we are careful to only set our properties.
  if (window.Square) {
    window.Square.bridge = bridge;
  } else {
    window.Square = { bridge };
  }

  bridge.addon.onAddonRequest = async (request, onResolve, onReject) => {
    let onAddonRequestCallback;
    switch (request.actionName) {
      case SDKAction.SDK_GET_ADDON_INFO: {
        onAddonRequestCallback = callbacks[SDKAction.SDK_GET_ADDON_INFO];
        break;
      }

      case SDKAction.DEVICE_NOTIFY_BARCODE_SCANNED: {
        onAddonRequestCallback = callbacks[
          SDKAction.DEVICE_NOTIFY_BARCODE_SCANNED
        ].bind(
          this,
          (request as NotifyBarcodeScannedRequest).value.scannedCode
        );
        break;
      }

      case SDKAction.DEVICE_GET_RECEIPTS: {
        onAddonRequestCallback = callbacks[SDKAction.DEVICE_GET_RECEIPTS];
        break;
      }

      default:
    }
    if (onAddonRequestCallback) {
      try {
        onResolve(await onAddonRequestCallback());
      } catch (error) {
        onReject(error as Error);
      }
    } else {
      onReject(new Error('action unhandled by the addon.'));
    }
  };

  const response = (await bridge.app.postMessage(
    new InitializeRequest()
  )) as InitializeResponse;

  if (response.value.reverseProxyData) {
    bridge.addon.setReverseProxyData(response.value.reverseProxyData);
  }

  // Log context.
  log.logContext(response.value.context);

  return response.value.context;
}

const sdk: SdkApi = {
  initialize,
};

export { sdk };
